/**
 * Layout Component
 * Provides navigation and common layout structure for authenticated pages
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { auth, PortalUser } from '../auth';
import NotificationsPanel from './NotificationsPanel';
import MobileNav from './MobileNav';
import { useNotifications } from '../contexts/NotificationsContext';
import { useAutoHideOnScroll } from '../hooks/useAutoHideOnScroll';
import { 
  LayoutDashboard, 
  Lightbulb, 
  Activity, 
  ClipboardList, 
  Users, 
  Map,
  Bell,
  Target,
  PlayCircle,
  Settings as SettingsIcon
} from 'lucide-react';
import { useTour } from './tour/TourProvider';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<PortalUser | null>(null);
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);
  const { unreadCount } = useNotifications();
  const isNavVisible = useAutoHideOnScroll();
  const { startTour } = useTour();

  const handleStartTour = () => {
    navigate('/dashboard');
    setTimeout(() => startTour(), 500);
  };

  // For admin users, get the selected client info
  const selectedClient = auth.isAdminPortalUser() ? auth.getSelectedClient() : null;
  const isAdminSession = auth.isAdminPortalUser();

  useEffect(() => {
    const currentUser = auth.getUser();
    setUser(currentUser);
    
    // For admin users, redirect to client selection if no client selected
    if (auth.isAdminPortalUser() && !auth.hasSelectedClient()) {
      navigate('/select-client');
    }
  }, [navigate]);

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path ? 'active' : '';
  };

  const toggleNotifications = () => {
    setNotificationsPanelOpen(!notificationsPanelOpen);
  };

  return (
    <div className="layout">
      {/* Header */}
      <header className={`layout-header ${!isNavVisible ? 'nav-hidden' : ''}`}>
        <div className="header-content">
          <div className="header-left">
            <img 
              src="/HEATLogo_transparent.png" 
              alt="HEAT" 
              className="header-logo"
            />
            {/* Show client name - either from user.client or selectedClient for admins */}
            {(user?.client || selectedClient) && (
              <span className="header-client">
                {selectedClient?.name || user?.client?.name}
              </span>
            )}
            {isAdminSession && (
              <span className="admin-session-badge">Admin View</span>
            )}
          </div>
          
          <div className="header-right">
            {/* Notification Bell with Badge */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={toggleNotifications}
                className="notification-bell-button"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Bell size={20} style={{ color: '#374151' }} />
                {unreadCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      backgroundColor: '#f97316',
                      color: 'white',
                      borderRadius: '9999px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      minWidth: '16px',
                      height: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px'
                    }}
                  >
                    {unreadCount}
                  </span>
                )}
              </button>
              <NotificationsPanel
                isOpen={notificationsPanelOpen}
                onClose={() => setNotificationsPanelOpen(false)}
              />
            </div>
            
            <div className="user-info">
              <span className="user-name">{user?.fullName}</span>
              {isAdminSession && (
                <button 
                  onClick={() => {
                    auth.clearSelectedClient();
                    navigate('/select-client');
                  }} 
                  className="switch-client-button"
                  title="Switch Client"
                >
                  Switch Client
                </button>
              )}
              <button onClick={handleStartTour} className="tour-button" title="Take a Tour">
                <PlayCircle size={16} />
                <span>Tour</span>
              </button>
              <button onClick={handleLogout} className="logout-button">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <div className="layout-main">
        {/* Sidebar navigation */}
        <aside className="layout-sidebar">
          {(user?.client || selectedClient) && (
            <div className="sidebar-client">
              <div className="sidebar-client-name">
                {selectedClient?.name || user?.client?.name}
              </div>
              {user?.client?.industry && !isAdminSession && (
                <div className="sidebar-client-industry">{user.client.industry}</div>
              )}
              {isAdminSession && (
                <div className="sidebar-admin-indicator">Viewing as Admin</div>
              )}
            </div>
          )}
          <nav className="sidebar-nav">
            <Link to="/dashboard" className={`nav-item ${isActive('/dashboard')}`} data-tour="sidebar-dashboard">
              <LayoutDashboard className="nav-icon" size={20} />
              <span className="nav-label">Dashboard</span>
            </Link>
            
            <Link to="/insights" className={`nav-item ${isActive('/insights')}`}>
              <Lightbulb className="nav-icon" size={20} />
              <span className="nav-label">Insights</span>
            </Link>
            
            <Link to="/decision-board" className={`nav-item ${isActive('/decision-board')}`}>
              <Target className="nav-icon" size={20} />
              <span className="nav-label">Decision Board</span>
            </Link>
            
            <Link to="/monitoring" className={`nav-item ${isActive('/monitoring')}`} data-tour="sidebar-monitoring">
              <Activity className="nav-icon" size={20} />
              <span className="nav-label">Monitoring</span>
            </Link>
            
            <Link to="/issues" className={`nav-item ${isActive('/issues')}`} data-tour="sidebar-issues">
              <ClipboardList className="nav-icon" size={20} />
              <span className="nav-label">Issues</span>
            </Link>
            
            <Link to="/stakeholders" className={`nav-item ${isActive('/stakeholders')}`} data-tour="sidebar-stakeholders">
              <Users className="nav-icon" size={20} />
              <span className="nav-label">Stakeholders</span>
            </Link>
            
            <Link to="/stakeholder-map" className={`nav-item ${isActive('/stakeholder-map')}`} data-tour="sidebar-stakeholder-map">
              <Map className="nav-icon" size={20} />
              <span className="nav-label">Stakeholder Map</span>
            </Link>
            
            <Link to="/settings" className={`nav-item ${isActive('/settings')}`}>
              <SettingsIcon className="nav-icon" size={20} />
              <span className="nav-label">Settings</span>
            </Link>
          </nav>
        </aside>

        {/* Page content */}
        <main className="layout-content">
          {children}
        </main>
      </div>

      {/* Mobile Navigation - Only visible on screens < 1024px */}
      <MobileNav isVisible={isNavVisible} />
    </div>
  );
};

export default Layout;
